import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getUserProfile } from '../api';
import Clarity from '@microsoft/clarity';

interface User {
  userId: number;
  email: string;
  firstName?: string;
  lastName?: string;
  customerId?: number;
  customerName?: string;
  role?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
  setCustomerName: (name: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on initial load
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setToken(storedToken);
          setUser(parsedUser);
          setIsAuthenticated(true);

          // Validate token with the backend
          try {
            // Call the getUserProfile API which requires authentication
            await getUserProfile();
            // If the call succeeds, the token is valid
            console.log('Token validated successfully');
          } catch (error) {
            console.error('Token validation failed:', error);
            // If the call fails, the token is invalid or expired
            logout();
          }
        } catch (error) {
          console.error('Error parsing stored user:', error);
          // Clear invalid data
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }

      // Authentication check is complete, regardless of outcome
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  // Add this helper function to identify users across both analytics platforms
  const identifyUser = (user: User) => {
    if (process.env.NODE_ENV === 'production') {
      // Clarity identification
      Clarity.identify(user.userId.toString());
      Clarity.setTag('customer_id', user.customerId?.toString() || '');
      Clarity.setTag('user_role', user.role || '');
      Clarity.setTag('user_email', user.email);

      // Google Analytics identification
      if (window.gtag) {
        window.gtag('config', 'G-4YCB915L6X', { // Replace with your GA4 ID
          user_id: user.userId.toString(),
          custom_map: {
            'customer_id': user.customerId?.toString() || '',
            'user_role': user.role || '',
            'customer_name': user.customerName || ''
          }
        });
      }
    }
  };

  const login = (newToken: string, newUser: User) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);

    // Identify user across analytics platforms
    identifyUser(newUser);

    setIsAuthenticated(true);
    setIsLoading(false);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setIsLoading(false);
  };

  const setCustomerName = (name: string) => {
    if (user) {
      const updatedUser = { ...user, customerName: name };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, isLoading, login, logout, setCustomerName }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
